import { useState } from "react";
import { IoClose } from "react-icons/io5";
import { toast } from "react-hot-toast";
import TextAreaInput from "./TextAreaInput";
import axios from "axios";
import { useAuthStore } from "../../context/useAuthStore"; 

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface AddCustomQuestionModalProps {
  showModal: boolean;
  onClose: () => void;
  subjectOptions: { label: string; value: string }[]; 
}

const AddCustomQuestionModal: React.FC<AddCustomQuestionModalProps> = ({
  showModal,
  onClose,
  subjectOptions,
}) => {
  const [question, setQuestion] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const { user, token, clearSession } = useAuthStore();

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuestion(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = question.trim();
    if (!trimmed) {
      setError(true);
      toast.error("Please enter a question.");
      return;
    }

    setLoading(true);
    setError(false);

    try {
      const subjectId = subjectOptions[0]?.value;
      const userId = user?.userId;

      if (!subjectId || !userId) throw new Error("Subject or user missing.");

      const response = await axios.post(
        `${API_BASE_URL}/customquestion`,
        {
          subjectId,
          customQuestion: trimmed,
          userId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      if (response.data?.success) {
        toast.success("New question added successfully!");
        setQuestion("");
        onClose();
      } else {
        toast.error(response.data?.message || "Failed to add question.");
      }
    } catch (err: any) {
      
      if (axios.isAxiosError(err)) {
        const errorMessage = err.response?.data?.message || "Something went wrong.";
        
        
        if (errorMessage.includes('Session expired') || err.response?.status === 401) {
          clearSession('expired');
          onClose(); 
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!showModal) return null;

  return (
    <div className="modalSection">
      <div className="modalContainer">
        <div className="flex justify-between items-center gap-2 border-b pb-1 border-gray-200 mb-2">
          <h2 className="text-lg font-semibold">Add Custom Question</h2>
          <button onClick={onClose} className="modalCloseButton">
          <IoClose size={20} />
        </button>
        </div>

        <h2 className="customQuestionModalSubject">Subject:<span className="customQuestionModalValue">{subjectOptions[0]?.label}</span></h2>

        <form onSubmit={handleSubmit} className="modalForm">
          <TextAreaInput
            rows={6}
            id="question"
            name="question"
            value={question}
            onChange={handleTextAreaChange}
            placeholder="Enter your custom question"
            error={error}
            errorMessage="Field is required, Please enter your custom question"
            disabled={loading}
          />
          <button
            type="submit"
            className={`defaultButton ${
              loading || !question.trim() ? "disabled" : ""
            }`}
            disabled={loading || !question.trim()}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddCustomQuestionModal;